import { FieldArrayPath, FieldPath, FieldValues, Path } from "react-hook-form";
import { SelectOption, SelectValueType } from "../control";
import { OptionsOrGroups } from "../select";

export function pathToArray<TFieldValues extends FieldValues>(
  path: Path<TFieldValues> | Path<TFieldValues>[],
): Path<TFieldValues>[];
export function pathToArray<TFieldValues extends FieldValues>(
  path?: Path<TFieldValues> | Path<TFieldValues>[],
): Path<TFieldValues>[] | undefined;
export function pathToArray<TFieldValues extends FieldValues>(
  path?: Path<TFieldValues> | Path<TFieldValues>[],
): Path<TFieldValues>[] | undefined {
  if (path == null) {
    return;
  }

  if (Array.isArray(path)) {
    return path;
  }

  return [path];
}

export function getFieldArrayValidationNames<
  TFieldValues extends FieldValues,
>(
  fieldArrayName: FieldArrayPath<TFieldValues>,
  rowCount: number,
  rowControlName: string,
): FieldPath<TFieldValues>[] {
  const lastSeparatorIndex = fieldArrayName.lastIndexOf(".");
  const parentName =
    lastSeparatorIndex < 0
      ? undefined
      : fieldArrayName.slice(0, lastSeparatorIndex);
  const rowNames = Array.from(
    { length: rowCount },
    (_, rowIndex) => `${fieldArrayName}.${rowIndex}.${rowControlName}`,
  );

  if (parentName) {
    // The ancestor avoids RHF double-wrapping an existing array root error.
    // Registered row descendants let the resolver identify the field array.
    return [parentName, ...rowNames] as FieldPath<TFieldValues>[];
  }

  return (
    rowNames.length ? rowNames : [fieldArrayName]
  ) as FieldPath<TFieldValues>[];
}

export function filterOption(values: SelectValueType[], option: SelectOption) {
  return (
    !option.parentItemIds?.length ||
    option.parentItemIds.some((id) => values.includes(id))
  );
}

export function filterOptions(
  values: SelectValueType[],
  options: OptionsOrGroups,
): OptionsOrGroups {
  return options
    .map((option) => {
      if ("options" in option) {
        return {
          label: option.label,
          options: option.options.filter((x) => filterOption(values, x)),
        };
      }
      return option;
    })
    .filter((option) => {
      if ("options" in option) {
        return !!option.options.length;
      }

      return filterOption(values, option);
    });
}

export function filterValues(
  value: SelectValueType,
  options: OptionsOrGroups,
): SelectValueType {
  if (value == null) return;

  const ids = new Set(
    options
      .flatMap((option) => {
        if ("options" in option) return option.options;
        return option;
      })
      .map((option) => option.id),
  );

  if (Array.isArray(value)) {
    return value.filter((value) => ids.has(value));
  }

  if (!ids.has(value)) {
    return;
  }

  return value;
}

export function areValuesEqual(left: SelectValueType, right: SelectValueType) {
  if (Array.isArray(left) && Array.isArray(right)) {
    return (
      left.length === right.length && left.every((left) => right.includes(left))
    );
  }

  if (typeof left === "string" && typeof right === "string" && left === right) {
    return true;
  }

  return false;
}
